import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";

import { humanifyDate } from "@/src/helpers/utils";
import { useEditorContext } from "@/src/contexts/editorContext";
import UpgradePopup from "@/src/components/Popups/UpgradePopup";

import { SheetHeader, SheetTitle } from "@/src/components/ui/sheet";
import { handleManageAccount } from "@/src/helpers/stripe";
import { captureEvent } from "@/src/helpers/trackEvent";

// Deliberately no feature list. The upgrade button opens UpgradePopup, which
// makes the full pitch — repeating it here in different words just contradicted
// it at 390px. This card's job is: what you're on, what it costs, the door.
function ProUpsell({ onUpgrade }) {
  return (
    <div
      className="qk-hard-shadow-sm mx-auto w-full rounded-xl p-4"
      style={{ background: "var(--qk-paper-3)", border: "1px solid var(--qk-rule)" }}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="qk-display" style={{ fontSize: 22, fontStyle: "italic", color: "var(--qk-ink)" }}>
          Quarkle Pro
        </span>
        <span
          className="qk-caps shrink-0 rounded-full px-2 py-0.5"
          style={{ fontSize: 9, background: "var(--qk-lime)", color: "var(--qk-on-lime)" }}
        >
          upgrade
        </span>
      </div>

      <div className="mb-2 mt-1 flex items-baseline gap-1.5">
        <span className="qk-display" style={{ fontSize: 26, color: "var(--qk-ink)" }}>
          $4.99
        </span>
        <span className="qk-mono" style={{ fontSize: 11, color: "var(--qk-muted)" }}>
          first month · then $19.99
        </span>
      </div>

      <p className="mb-4" style={{ fontSize: 12, lineHeight: 1.5, color: "var(--qk-muted)" }}>
        Open expression, every model, the deeper editorial reports, and 200x the daily usage.
      </p>

      <button
        type="button"
        onClick={onUpgrade}
        className="qk-pill w-full"
        style={{ background: "var(--qk-pink-deep)", color: "var(--qk-on-pink)", borderColor: "var(--qk-pink-deep)" }}
      >
        upgrade to pro
      </button>

      <div className="mt-2.5 flex items-center justify-between">
        <Link
          href="/pro"
          target="_blank"
          className="qk-caps flex items-center gap-1"
          style={{ fontSize: 9, color: "var(--qk-muted)" }}
        >
          what pro unlocks
          <ArrowUpRight className="h-3 w-3" />
        </Link>
        <span className="qk-mono" style={{ fontSize: 10, color: "var(--qk-muted)" }}>
          or $120/yr · save 50%
        </span>
      </div>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-2" style={{ borderTop: "1px solid var(--qk-rule)" }}>
      <span className="qk-caps shrink-0" style={{ fontSize: 9, color: "var(--qk-muted)" }}>
        {label}
      </span>
      <span className="qk-mono truncate text-right" style={{ fontSize: 11, color: "var(--qk-ink)" }}>
        {value}
      </span>
    </div>
  );
}

// Paid subscribers get their actual account state here. The plan's feature list
// is a sales pitch — useful on the basic tier, pure noise to someone who already
// bought it and came looking for their renewal date.
function SubscriptionDetails({ subscription, isTrial }) {
  const start = subscription?.stripe_current_period_start;
  const end = subscription?.stripe_current_period_end;
  const cancelling = subscription?.stripe_cancel_at_period_end === true;

  const periodDays =
    start && end ? Math.round((new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24)) : null;
  const cycle = periodDays == null ? null : periodDays >= 300 ? "yearly" : "monthly";

  const status = isTrial ? "free trial" : cancelling ? "cancels at period end" : subscription?.stripe_subscription_status || "active";

  return (
    <div
      className="qk-hard-shadow-sm mx-auto w-full rounded-xl p-4"
      style={{ background: "var(--qk-paper-3)", border: "1px solid var(--qk-rule)" }}
    >
      <div className="flex items-center justify-between gap-3 pb-3">
        <span className="qk-display" style={{ fontSize: 20, fontStyle: "italic", color: "var(--qk-ink)" }}>
          Quarkle Pro
        </span>
        <span
          className="qk-caps shrink-0 rounded-full px-2.5 py-1"
          style={{
            fontSize: 9,
            background: isTrial || cancelling ? "transparent" : "var(--qk-lime)",
            border: `1px solid ${isTrial || cancelling ? "var(--qk-rule)" : "var(--qk-lime)"}`,
            color: isTrial || cancelling ? "var(--qk-muted)" : "var(--qk-on-lime)",
          }}
        >
          {isTrial ? "trial" : cancelling ? "ending" : "active"}
        </span>
      </div>

      {cycle && <DetailRow label="billing" value={cycle} />}
      {end && <DetailRow label={cancelling || isTrial ? "access until" : "renews"} value={humanifyDate(end)} />}
      <DetailRow label="status" value={status} />
      {subscription?.created_at && <DetailRow label="member since" value={humanifyDate(subscription.created_at)} />}
    </div>
  );
}

export default function ManageSubscriptionContent() {
  const { getAccessTokenSilently, logout } = useAuth0();
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { subscription } = useEditorContext();
  const router = useRouter();
  const userPlan = subscription?.subscription_type;
  const isBasic = userPlan !== "pro";
  const isTrialSubscription = subscription?.subscription_type === "pro" && subscription?.stripe_subscription_status === "trialing";

  function openUpgrade() {
    captureEvent("upgrade_cta_clicked", { surface: "manage_subscription", current_plan: userPlan });
    setShowUpgradePopup(true);
  }

  function handleCustomerPortal() {
    return handleManageAccount(subscription, router, setError, setIsLoading, getAccessTokenSilently, logout);
  }

  return (
    <SheetHeader>
      <SheetTitle>Manage Subscription</SheetTitle>
      <p className="qk-caps text-center" style={{ color: "var(--qk-muted)", marginBottom: 6 }}>
        {isBasic ? "you're on quarkle free" : "current subscription"}
      </p>
      <div className="flex flex-col items-center justify-center">
        {isBasic ? (
          <ProUpsell onUpgrade={openUpgrade} />
        ) : (
          <SubscriptionDetails subscription={subscription} isTrial={isTrialSubscription} />
        )}
      </div>
      <div className="py-4" />
      {isBasic && (
        <UpgradePopup
          open={showUpgradePopup}
          closeModal={() => setShowUpgradePopup(false)}
          page="manage_subscription"
        />
      )}
      {!isBasic && (
        <button
          type="button"
          onClick={handleCustomerPortal}
          className="qk-pill mx-auto flex items-center gap-2"
        >
          {!isLoading && !error && <span>manage account</span>}
          {isLoading && !error && <i className="pi pi-spinner pi-spin" style={{ color: "var(--qk-ink)" }}></i>}
          {error && (
            <span className="qk-caps" style={{ color: "var(--qk-clay)" }}>
              error, try again
            </span>
          )}
        </button>
      )}
    </SheetHeader>
  );
}
